import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { EyeOff, Loader2, Trophy } from "lucide-react";
import { Section, SectionTitle, Reveal } from "../components/UIBits";
import { TOURNAMENT } from "../lib/constants";
import { useT } from "../lib/i18n";
import api from "../lib/api";

const SORTS = [
    { key: "PTS", labelFr: "Points", labelEn: "Points" },
    { key: "G",   labelFr: "Buts",   labelEn: "Goals" },
    { key: "A",   labelFr: "Passes", labelEn: "Assists" },
];

export default function ScorersPage() {
    const { t, lang } = useT();
    const [sort, setSort] = useState("PTS");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        api.get(`/scorers/by_category?sort=${sort}&limit=10`)
            .then((r) => setData(r.data))
            .finally(() => setLoading(false));
        const t = setInterval(() => {
            api.get(`/scorers/by_category?sort=${sort}&limit=10`).then((r) => setData(r.data));
        }, 30000);
        return () => clearInterval(t);
    }, [sort]);

    const cats = data?.categories?.length ? data.categories : TOURNAMENT.categories;

    return (
        <div data-testid="scorers-page">
            <Helmet><title>{`${t("scorers.title_pre")} ${t("scorers.title_accent")} — Défi Ultime`}</title></Helmet>
            <Section>
                <SectionTitle kicker={t("scorers.kicker")} title={t("scorers.title_pre")} accent={t("scorers.title_accent")} />

                {/* Sort control */}
                <div className="flex flex-wrap items-center gap-2 mb-6">
                    <span className="text-xs uppercase tracking-widest text-white/40 mr-2">
                        {lang === "en" ? "Sort by" : "Trier par"} :
                    </span>
                    {SORTS.map((s) => (
                        <button
                            key={s.key}
                            onClick={() => setSort(s.key)}
                            className={`px-4 py-2 font-display text-sm sm:text-base tracking-widest uppercase angle-tag transition-all ${
                                sort === s.key ? "bg-[#7FD93C] text-black" : "bg-white/5 text-white/70 hover:bg-white/10"
                            }`}
                            data-testid={`sort-${s.key}`}
                        >
                            {lang === "en" ? s.labelEn : s.labelFr}
                        </button>
                    ))}
                </div>

                {data && !data.public ? (
                    <div className="glass-card p-10 text-center">
                        <EyeOff className="mx-auto mb-4 text-white/40" size={32} />
                        <div className="font-display text-2xl tracking-widest text-white/60">{t("scorers.stats_hidden")}</div>
                    </div>
                ) : loading && !data ? (
                    <div className="flex items-center justify-center py-16"><Loader2 className="animate-spin text-[#4FE0D5]" size={32} /></div>
                ) : (
                    <div className="grid gap-10">
                        {cats.map((cat) => {
                            const rows = (data?.by_category?.[cat]) || [];
                            return (
                                <Reveal key={cat}>
                                    <CategoryBlock cat={cat} rows={rows} sort={sort} lang={lang} t={t} />
                                </Reveal>
                            );
                        })}
                    </div>
                )}
            </Section>
        </div>
    );
}

function CategoryBlock({ cat, rows, sort, lang, t }) {
    return (
        <div data-testid={`scorers-block-${cat}`}>
            <div className="flex items-center gap-3 mb-4">
                <Trophy size={20} className="text-[#7FD93C]" />
                <h3 className="font-display text-3xl sm:text-4xl uppercase tracking-widest">
                    {cat} <span className="text-white/40 text-lg">— {lang === "en" ? "Top 10" : "Top 10"}</span>
                </h3>
            </div>

            {rows.length === 0 ? (
                <div className="text-white/40 text-sm italic pl-8">
                    {lang === "en" ? "No stats recorded yet." : "Aucune statistique enregistrée pour le moment."}
                </div>
            ) : (
                <>
                    {/* Desktop table */}
                    <div className="overflow-x-auto hidden md:block">
                        <table className="tx-table" data-testid={`scorers-table-${cat}`}>
                            <thead>
                                <tr>
                                    <th>{t("scorers.col_rank")}</th>
                                    <th>{t("scorers.col_player")}</th>
                                    <th>{t("scorers.col_team")}</th>
                                    <th className={sort === "G" ? "text-[#7FD93C]" : ""}>{t("scorers.col_goals")}</th>
                                    <th className={sort === "A" ? "text-[#7FD93C]" : ""}>{t("scorers.col_assists")}</th>
                                    <th className={sort === "PTS" ? "text-[#7FD93C]" : ""}>{t("scorers.col_points")}</th>
                                    <th>{t("scorers.col_pim")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r, i) => (
                                    <tr key={r.player_id} data-testid={`row-${cat}-${i}`}>
                                        <td className="font-display text-2xl text-[#7FD93C]">{i + 1}</td>
                                        <td><span className="font-semibold">{r.name}</span> <span className="text-white/40 text-xs">#{r.number}</span></td>
                                        <td className="text-white/70">{r.team_name}</td>
                                        <td className={sort === "G" ? "font-display text-2xl text-[#4FE0D5]" : ""}>{r.G}</td>
                                        <td className={sort === "A" ? "font-display text-2xl text-[#4FE0D5]" : ""}>{r.A}</td>
                                        <td className={sort === "PTS" ? "font-display text-2xl text-[#4FE0D5]" : ""}>{r.PTS}</td>
                                        <td>{r.PIM}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Mobile cards */}
                    <div className="md:hidden grid gap-3" data-testid={`scorers-cards-${cat}`}>
                        {rows.map((r, i) => (
                            <div key={r.player_id} className="glass-card p-4">
                                <div className="flex items-center gap-3 mb-3">
                                    <div className="font-display text-3xl text-[#7FD93C] w-8 text-center shrink-0">{i + 1}</div>
                                    <div className="flex-1 min-w-0">
                                        <div className="font-semibold truncate">{r.name} <span className="text-white/40 text-xs">#{r.number}</span></div>
                                        <div className="text-xs text-white/60 truncate">{r.team_name}</div>
                                    </div>
                                    <div className="text-right shrink-0">
                                        <div className="font-display text-2xl text-[#4FE0D5] leading-none">
                                            {sort === "G" ? r.G : sort === "A" ? r.A : r.PTS}
                                        </div>
                                        <div className="text-[10px] text-white/40 uppercase tracking-widest">
                                            {sort === "G" ? t("scorers.col_goals") : sort === "A" ? t("scorers.col_assists") : t("scorers.col_points")}
                                        </div>
                                    </div>
                                </div>
                                <div className="grid grid-cols-4 gap-1 text-center text-xs">
                                    <Stat label={t("scorers.col_goals")} value={r.G} highlighted={sort === "G"} />
                                    <Stat label={t("scorers.col_assists")} value={r.A} highlighted={sort === "A"} />
                                    <Stat label={t("scorers.col_points")} value={r.PTS} highlighted={sort === "PTS"} />
                                    <Stat label={t("scorers.col_pim")} value={r.PIM} />
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}

function Stat({ label, value, highlighted }) {
    return (
        <div>
            <div className={`font-bold text-sm ${highlighted ? "text-[#7FD93C]" : ""}`}>{value}</div>
            <div className="text-white/40 text-[9px] uppercase tracking-wider">{label}</div>
        </div>
    );
}
